import {
  useEffect,
  useState,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react';
import { notification } from 'antd';
import useShortcut from '@/hooks/useShortCutHook';
import Hls from 'hls.js';

export const defaultVideoHeight = 720;

export default function useVideoPlayer({
  urls,
  videoRef,
  playing,
  handleFullScreen,
  onReady,
  onStart,
  onPlay,
  onProgress,
  onRestart,
  seekPosition,
}) {
  const onErrorTimeoutRef = useRef(null);

  const [state, setState] = useState({
    ready: false,
    playing: false,
    volume: 1,
    bufferTime: 0,
    duration: 0,
    currentTime: 0,
    fullScreen: false,
    videoWidth: 0,
    videoHeight: 0,
    heightBeforeFullScreen: 0,
    horizontalPadding: 0,
    playbackRate: 1,
    hlsReady: false,
    forceMP4: false,
    videoKey: null,
  });
  const actions = {
    setState: (newState) => {
      setState((previousState) => ({ ...previousState, ...newState }));
    },
    onDuration: (duration) => {
      actions.setState({
        duration,
        ready: true,
      });
      if (onReady) onReady(duration);
    },
    onStart: onStart ? onStart : () => {},
    onProgress: ({ playedSeconds, loadedSeconds }) => {
      actions.setState({
        currentTime: playedSeconds,
        bufferTime: loadedSeconds,
      });
      if (onProgress) {
        onProgress({ playedSeconds, duration: state.duration });
      }
    },
    play: () => {
      actions.setState({ playing: true });
      //pressing play at the end of the video restarts the video. Call the the parent onPlay then.
      if (state.currentTime >= state.duration) {
        actions.restart();
      }
      if (onPlay) onPlay();
    },
    restart: () => {
      actions.setState({ currentTime: 0 });
      actions.seek(0);
      if (onRestart) onRestart();
    },
    pause: () => {
      actions.setState({ playing: false });
    },
    playPause: () => {
      if (state.playing) {
        actions.pause();
      } else {
        actions.play();
      }
    },
    seek: (position) => {
      videoRef.current.seekTo(position, 'seconds');
    },
    onFullScreenChange: useCallback((isFullScreen) => {
      actions.setState({
        fullScreen: isFullScreen,
      });
    }),
    onFullScreen: async () => {
      if (state.fullScreen) {
        try {
          await handleFullScreen.exit();
        } catch (e) {
          try {
            const element = document.body;
            const requestMethod =
              element.exitFullscreen ||
              element.mozCancelFullScreen ||
              element.msExitFullscreen;

            if (requestMethod) {
              requestMethod.apply(document);
            } else if (
              videoRef.current.getInternalPlayer().webkitExitFullscreen
            ) {
              videoRef.current.getInternalPlayer().webkitExitFullscreen();
            } else {
              notification.info({
                message: 'This feature is not available for this device',
              });
            }
          } catch (e) {
            notification.info({
              message: 'This feature is not available for this device',
            });
          }
        }
      } else {
        actions.setState({
          heightBeforeFullScreen: state.videoHeight,
        });
        try {
          await handleFullScreen.enter();
        } catch (e) {
          try {
            const element = document.body;
            const requestMethod =
              element.requestFullScreen ||
              element.webkitRequestFullscreen ||
              element.webkitRequestFullScreen ||
              element.mozRequestFullScreen ||
              element.msRequestFullscreen;

            if (requestMethod) {
              requestMethod.apply(element);
            } else if (
              videoRef.current.getInternalPlayer().webkitEnterFullScreen
            ) {
              videoRef.current.getInternalPlayer().webkitEnterFullScreen();
            } else {
              notification.info({
                message: 'This feature is not available for this device',
              });
            }
          } catch (e) {
            notification.info({
              message: 'This feature is not available for this device',
            });
          }
        }
      }
    },
    onVideoUrlChange: (videoUrl) => {
      actions.setState({
        url: videoUrl?.url,
      });
    },
    changePlaybackRate: (playbackRate) => {
      actions.setState({ playbackRate });
    },
    onError: () => {
      handleOnError();
    },
  };

  const handleOnError = useCallback(() => {
    onErrorTimeoutRef.current = setTimeout(() => {
      if (state.currentTime == 0) {
        actions.setState({ forceMP4: true });
      }
    }, 3000);
  }, [state.currentTime]);

  useEffect(() => {
    actions.seek(0);
    actions.setState({
      currentTime: 0,
    });
  }, [state.duration]);

  useEffect(() => {
    actions.setState({ playing });
  }, [playing]);

  useEffect(() => {
    if (!state.fullScreen) {
      //handleResize();
      actions.setState({
        heightBeforeFullScreen: 0,
      });
    }
  }, [state.fullScreen]);

  const onSpacePress = useCallback(() => {
    actions.playPause();
  });

  useShortcut({
    shortcut: ' ',
    handler: onSpacePress,
    focusTagExclude: ['INPUT'],
  });

  useEffect(() => {
    if (urls) {
      let urlsToUse = [];
      let urlObject = null;
      const hlsUrl = urls.find((url) => url.type == 'VideoHLS');
      if (hlsUrl && Hls.isSupported() && !state.forceMP4) {
        urlsToUse.push(hlsUrl);
        urlObject = hlsUrl;
      } else {
        urlsToUse = urls.filter((url) => url.type != 'VideoHLS');
        urlObject =
          urlsToUse?.find((url) => url.type == defaultVideoHeight) ||
          urlsToUse?.[0];
      }

      actions.setState({
        urls: urlsToUse,
        url: urlObject?.url,
        videoKey: urlObject?.url,
      });
    }
  }, [urls, state.forceMP4]);

  useLayoutEffect(() => {
    actions.seek(state.currentTime);
  }, [state.url]);

  useEffect(() => {
    if (seekPosition != null || seekPosition != undefined) {
      actions.seek(seekPosition);
    }
  }, [seekPosition]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.Hls = Hls;
      actions.setState({ hlsReady: true });
    }
    return () => {
      if (onErrorTimeoutRef.current) {
        clearTimeout(onErrorTimeoutRef.current);
      }
    };
  }, []);

  return { state, actions };

  /* useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []); */
  /* 
  function handleResize() {
    const maxVideoWidth = videoContainerRef.current?.offsetWidth;
    const maxVideoHeight =
      state.heightBeforeFullScreen || videoContainerRef.current?.offsetHeight;
    const maxRatio = maxVideoWidth / maxVideoHeight;
    const videoWidth = Math.floor(
      maxRatio >= ratio ? maxVideoHeight * ratio : maxVideoWidth
    );
    const videoHeight = Math.floor(
      maxRatio >= ratio ? maxVideoHeight : maxVideoWidth / ratio
    );
    const horizontalPadding = Math.max(
      0,
      Math.round((maxVideoWidth - videoWidth) / 2)
    );
    actions.setState({
      videoWidth,
      videoHeight,
      horizontalPadding,
    });
  } */
}
